/**
 * Survey API service for EEG Survey Integration.
 * Uses existing api.js pattern with axios and auth interceptors.
 * Kept separate from existing codebase.
 */
import api from './api';

export const surveyService = {
  // Get all active surveys
  getSurveys: async () => {
    const response = await api.get('/surveys/');
    return response.data;
  },

  // Get survey details with questions
  getSurvey: async (surveyId) => {
    const response = await api.get(`/surveys/${surveyId}/`);
    return response.data;
  },

  // Start survey session (create response)
  startSurvey: async (surveyId) => {
    const response = await api.post('/survey/responses/', {
      survey_id: surveyId,
    });
    return response.data;
  },

  // Submit answer immediately (incremental)
  submitAnswer: async (responseId, answerData) => {
    const response = await api.post(`/survey/responses/${responseId}/answers/`, {
      question_id: answerData.questionId,
      answer_text: answerData.answerText,
      question_displayed_at: answerData.questionDisplayedAt,
      answer_submitted_at: answerData.answerSubmittedAt,
    });
    return response.data;
  },

  // Update response status (e.g., mark as completed)
  updateResponse: async (responseId, status) => {
    const response = await api.patch(`/survey/responses/${responseId}/`, {
      status: status,
    });
    return response.data;
  },

  // Get user's survey responses
  getResponses: async () => {
    const response = await api.get('/survey/responses/list/');
    return response.data;
  },

  // Get specific response with answers
  getResponse: async (responseId) => {
    const response = await api.get(`/survey/responses/${responseId}/`);
    return response.data;
  },

  // Get response summary
  getResponseSummary: async (responseId) => {
    const response = await api.get(`/survey/responses/${responseId}/summary/`);
    return response.data;
  },

  // Export data (CSV/JSON)
  exportData: async (responseId, format = 'csv') => {
    const response = await api.get(`/survey/responses/${responseId}/export/`, {
      params: { format },
      responseType: format === 'csv' ? 'blob' : 'json',
    });
    return response.data;
  },

  // Submit EEG readings in bulk
  submitEEGReadings: async (readings) => {
    const response = await api.post('/eeg/readings/bulk/', {
      readings: readings,
    }, {
      timeout: 30000, // 30 seconds for bulk upload
    });
    return response.data;
  },

  // Get EEG readings for a response
  getEEGReadings: async (responseId, page = 1, pageSize = 1000, enableNormalization = true) => {
    const response = await api.get(`/survey/responses/${responseId}/eeg-readings/`, {
      params: { 
        page, 
        page_size: pageSize,
        enable_normalization: enableNormalization
      },
    });
    return response.data;
  },

  // Create EEG session
  createEEGSession: async (responseId, deviceId = null) => {
    const response = await api.post('/eeg/sessions/', {
      response: responseId,
      device_id: deviceId,
    });
    return response.data;
  },

  // Get question interpretation (AI)
  getQuestionInterpretation: async (responseId, questionId, enableNormalization = true) => {
    const response = await api.post(`/survey/responses/${responseId}/questions/${questionId}/interpretation/`, {
      enable_normalization: enableNormalization
    });
    return response.data;
  },

  // Generate comprehensive report (AI)
  generateComprehensiveReport: async (responseId, forceRegenerate = false, enableNormalization = true) => {
    const response = await api.post(`/survey/responses/${responseId}/comprehensive-report/`, {
      force_regenerate: forceRegenerate,
      enable_normalization: enableNormalization
    });
    return response.data;
  },

  // Delete a survey response
  deleteResponse: async (responseId) => {
    const response = await api.delete(`/survey/responses/${responseId}/`);
    return response.data;
  },

  // Redo report - clear stored interpretations and comprehensive report
  redoReport: async (responseId) => {
    const response = await api.post(`/survey/responses/${responseId}/redo-report/`);
    return response.data;
  },
};
